<div class="report-overview">
    <header class="report-header">
        <div>
            <span class="report-eyebrow">REPORTS / OVERVIEW</span>
            <h1>{{org?.name || 'Organization overview'}}</h1>
            <p>Device health, licenses, and organization resources at a glance.</p>
        </div>
        <div class="report-actions">
            <button mat-stroked-button (click)="loadData()" [disabled]="loading || templatesLoading">
                {{loading || templatesLoading ? 'Refreshing...' : 'Refresh'}}
            </button>
            <a mat-flat-button class="billing-link" [routerLink]="['/layout/report/billing/'+orgId]">Billing information <span aria-hidden="true">&#8599;</span></a>
        </div>
    </header>

    <div class="report-error" role="alert" *ngIf="loadError">
        <span>{{loadError}}</span><button mat-button (click)="loadData()">Try again</button>
    </div>
    <div class="loading-notice" role="status" *ngIf="loading">Loading your organization overview...</div>

    <div class="overview-charts">
        <section class="overview-panel" aria-labelledby="device-health-title" [attr.aria-busy]="loading">
            <div class="panel-heading">
                <div><h2 id="device-health-title">Device health</h2><p>Current status across your organization</p></div>
                <span class="panel-count" *ngIf="!loading && !loadError">{{deviceArray.length | number}} devices</span>
            </div>
            <div class="chart-and-legend health-content" [class.is-loading]="loading" [class.is-unavailable]="!!loadError">
                <div class="chart-wrapper"><canvas id="canvas" width="200" height="200" role="img" aria-label="Device health distribution. Counts are listed beside the chart."></canvas></div>
                <div class="detail-list status-legend" *ngIf="chart">
                    <div class="detail-list-item m-2">
                        <div class="square online"></div>
                        <div class="ml-2">Online</div>
                        <div class="count ml-2">{{upDeviceCnt}}</div>
                    </div>
                    <div class="detail-list-item m-2 flex-fill">
                        <div class="square offline"></div>
                        <div class="ml-2">Offline</div>
                        <div class="count ml-2">{{downDeviceCnt}}</div>
                    </div>
                    <div class="detail-list-item m-2 flex-fill">
                        <div class="square warning"></div>
                        <div class="ml-2">Warning</div>
                        <div class="count ml-2">{{warnDeviceCnt}}</div>
                    </div>
                    <div class="detail-list-item m-2 flex-fill">
                        <div class="square pending"></div>
                        <div class="ml-2">Pending</div>
                        <div class="count ml-2">{{pendingDeviceCnt}}</div>
                    </div>
                </div>
            </div>
            <p class="empty-state" *ngIf="!loading && !loadError && !deviceArray.length && !pendingDeviceCnt">No devices yet. Devices will appear here once added to this organization.</p>
        </section>

        <section class="overview-panel licenses-panel" aria-labelledby="licenses-title" [attr.aria-busy]="loading">
            <div class="panel-heading"><div><h2 id="licenses-title">Device licenses</h2><p>Base licenses and subscribed add-ons</p></div></div>
            <div class="chart-and-legend license-content" [class.is-loading]="loading" [class.is-unavailable]="!!loadError">
                <div class="chart-wrapper"><canvas id="license" width="200" height="200" role="img" aria-label="Device license distribution. Counts are listed beside the chart."></canvas></div>
                <div class="detail-list license-legend" *ngIf="chartLicense">
                    <div class="detail-list-item m-2">
                        <div class="square l_single_link"></div>
                        <div class="ml-2">Single Link</div>
                        <div class="count ml-2">{{l_single_link}}</div>
                    </div>
                    <div class="detail-list-item m-2 flex-fill">
                        <div class="square l_single_link_lte"></div>
                        <div class="ml-2">Single Link LTE</div>
                        <div class="count ml-2">{{l_single_link_lte}}</div>
                    </div>
                    <div class="detail-list-item m-2 flex-fill">
                        <div class="square l_failover"></div>
                        <div class="ml-2">Failover</div>
                        <div class="count ml-2">{{l_failover}}</div>
                    </div>
                    <div class="detail-list-item m-2">
                        <div class="square l_failover_lte"></div>
                        <div class="ml-2">Failover LTE</div>
                        <div class="count ml-2">{{l_failover_lte}}</div>
                    </div>
                    <div class="detail-list-item m-2 flex-fill">
                        <div class="square l_nfr"></div>
                        <div class="ml-2">NFR</div>
                        <div class="count ml-2">{{l_nfr}}</div>
                    </div>
                    <div class="detail-list-item m-2 flex-fill">
                        <div class="square l_spare"></div>
                        <div class="ml-2">Spare</div>
                        <div class="count ml-2">{{l_spare}}</div>
                    </div>
                    <div class="detail-list-item m-2">
                        <div class="square l_overlay"></div>
                        <div class="ml-2">Add-on: Overlay</div>
                        <div class="count ml-2">{{l_overlay}}</div>
                    </div>
                    <div class="detail-list-item m-2 flex-fill">
                        <div class="square l_cloud_dmz"></div>
                        <div class="ml-2">Add-on: Cloud DMZ</div>
                        <div class="count ml-2">{{l_cloud_dmz}}</div>
                    </div>
                    <div class="detail-list-item m-2 flex-fill">
                        <div class="square l_wan_connect"></div>
                        <div class="ml-2">Add-on: WAN Connect</div>
                        <div class="count ml-2">{{l_wan_connect}}</div>
                    </div>
                    <div class="detail-list-item m-2 flex-fill">
                        <div class="square l_1gb_lte_data"></div>
                        <div class="ml-2">Add-on: 1GB LTE Data</div>
                        <div class="count ml-2">{{l_1gb_lte_data}}</div>
                    </div>
                    <div class="detail-list-item m-2 flex-fill" *ngFor="let license of additionalLicenseCounts">
                        <div class="square" [style.background-color]="license.color"></div>
                        <div class="ml-2">{{license.label}}</div>
                        <div class="count ml-2">{{license.count}}</div>
                    </div>
                </div>
            </div>
            <p class="panel-note">A device can have more than one add-on license.</p>
        </section>
    </div>

    <div class="overview-resources">
        <section class="overview-panel" aria-labelledby="organizations-title" [attr.aria-busy]="loading">
            <div class="panel-heading"><div><h2 id="organizations-title">My organizations</h2><p>Organizations managed under this account</p></div></div>
            <div class="table-scroll"><table mat-table [dataSource]="orgCatDataSource" style="width: 100%;" [hidden]="loading || !!loadError">
                        <ng-container matColumnDef="type">
                            <th mat-header-cell *matHeaderCellDef>Organization Type</th>
                            <td mat-cell *matCellDef="let element">
                                {{ element.type }}
                            </td>
                        </ng-container>
                        <ng-container matColumnDef="count">
                            <th class="text-center" mat-header-cell *matHeaderCellDef>Organization Count</th>
                            <td class="text-center" mat-cell *matCellDef="let element">{{ element.count }}</td>
                        </ng-container>
                        <tr mat-header-row *matHeaderRowDef="displayedOrgCatColumns"></tr>
                        <tr mat-row *matRowDef="let row; columns: displayedOrgCatColumns"></tr>
                    </table></div>
            <div class="loading-placeholder" role="status" *ngIf="loading"><span>Loading organizations...</span><i></i><i></i></div>
            <p class="empty-state" *ngIf="!loading && !loadError && !orgCatDataSource.data.length">No child organizations to display.</p>
        </section>
        <section class="overview-panel" aria-labelledby="templates-title" [attr.aria-busy]="templatesLoading">
            <div class="panel-heading"><div><h2 id="templates-title">My templates</h2><p>Configuration templates and connected devices</p></div></div>
            <div class="table-scroll"><table mat-table [dataSource]="templateDataSource" style="width: 100%;" [hidden]="templatesLoading || !!templatesError || !!loadError">
                        <ng-container matColumnDef="t_type">
                            <th mat-header-cell *matHeaderCellDef>Template Type</th>
                            <td mat-cell *matCellDef="let element">
                                {{ element.t_type }}
                            </td>
                        </ng-container>
                        <ng-container matColumnDef="t_count">
                            <th class="text-center" mat-header-cell *matHeaderCellDef>Template Count</th>
                            <td class="text-center" mat-cell *matCellDef="let element">{{ element.t_count }}</td>
                        </ng-container>
                        <ng-container matColumnDef="t_device_count">
                            <th class="text-center" mat-header-cell *matHeaderCellDef>Connected Devices</th>
                            <td class="text-center" mat-cell *matCellDef="let element">{{element.t_device_count }}</td>
                        </ng-container>

                        <tr mat-header-row *matHeaderRowDef="displayedTemplateColumns"></tr>
                        <tr mat-row *matRowDef="let row; columns: displayedTemplateColumns"></tr>
                    </table></div>
            <div class="loading-placeholder" role="status" *ngIf="templatesLoading"><span>Loading templates...</span><i></i><i></i></div>
            <div class="report-error" role="alert" *ngIf="templatesError">{{templatesError}} <button mat-button (click)="loadData()">Retry</button></div>
        </section>
    </div>

    <section class="overview-panel report-details" aria-label="Detailed reports">
        <div class="panel-heading"><div><h2>Explore your organization</h2><p>Browse organizations, devices, and access history.</p></div></div>
            <mat-tab-group animationDuration="0ms" class="report-tabs" (selectedIndexChange)="onReportTabChange($event)">
        <mat-tab label="All Organizations">
            <ng-template matTabContent>
            <div class="mt-2" *ngIf="!loading && !loadError && allOrgData && deviceArray">
                <app-org-devices-table [dataSource]="orgDataSource" [deviceSource]="deviceArray" (refresh)="loadData()"
                    [selfSort]="true"></app-org-devices-table>
            </div>
            </ng-template>
        </mat-tab>
        <mat-tab label="Devices">
            <ng-template matTabContent>
            <div class="mt-2" *ngIf="!loading && !loadError && allOrgData && deviceArray">
                <app-device-table [dataSource]="deviceArray" [selfSort]="true" [isReportView]="true"></app-device-table>
            </div>
            </ng-template>
        </mat-tab>

        <mat-tab label="Jump Access Logs">
            <ng-template matTabContent>
            <mat-spinner *ngIf="historyLoading || loading" [diameter]="30" class="m-auto" aria-label="Loading jump access logs"></mat-spinner>
            <div class="report-error" role="alert" *ngIf="historyError">
                {{historyError}} <button mat-button (click)="loadHistory()">Retry</button>
            </div>
            <div class="mt-2" *ngIf="jumpDataSource">
                <app-device-browse-history [dataSource]="jumpDataSource" [selfSort]="true"></app-device-browse-history>
            </div>
            </ng-template>
        </mat-tab>
    </mat-tab-group>

    </section>
</div>
